import Skeleton from "common/components/ui/Skeleton";
import useEstimateCreation from "common/datahooks/useEstimateCreation";
import useEstimateUpdate from "common/datahooks/useEstimateUpdate";
import {
  formatPeriodUnitLabel,
  formatPrice,
  formatShortDate,
} from "common/helpers/utils";
import useDebounce from "common/hooks/useDebounce";
import type {
  PricingPeriodUnit,
  Subscription,
  SubscriptionType,
} from "common/types";

interface NewPriceProps {
  subscription: Subscription;
  type: SubscriptionType;
  newCount: number;
  selectedPeriodUnit: PricingPeriodUnit;
}

export default function NewPrice({
  subscription,
  type,
  newCount,
  selectedPeriodUnit,
}: NewPriceProps) {
  const [debouncedCount] = useDebounce(newCount);

  const { estimateUpdate, isLoadingEstimateUpdate } = useEstimateUpdate({
    subscription,
    type,
    selectedPeriodUnit,
    quantity: debouncedCount,
    prorate: false,
  });

  const { estimateCreation, isLoadingEstimateCreation } = useEstimateCreation({
    subscription,
    type,
    selectedPeriodUnit,
    quantity: debouncedCount,
  });

  const isLoading = isLoadingEstimateCreation || isLoadingEstimateUpdate;

  const isCancelled = subscription?.status === "cancelled";
  const isInTrial = subscription?.status === "in_trial";

  const estimate = estimateCreation ?? estimateUpdate;
  const nextBillingAt = estimate?.nextBillingAt;
  const amountDue = estimate?.amountDue ?? 0;

  const priceTitle = isCancelled ? "Price" : "New price";

  let startingDateComponent = null;
  if (selectedPeriodUnit === "year") {
    startingDateComponent = (
      <span className="text-xs-bold text-purple-600">
        Effective immediately
      </span>
    );
  } else {
    startingDateComponent = isLoading ? (
      <Skeleton className="h-4 w-40" />
    ) : (
      <span className="text-xs-bold text-purple-600">
        Starting from {formatShortDate(nextBillingAt * 1000)}
      </span>
    );
  }

  return (
    <div className="flex items-center justify-between px-2 py-1">
      <div className="flex flex-col gap-y-0.5">
        <span className="text-button-sm text-black-600">{priceTitle}</span>
        {isInTrial && startingDateComponent}
      </div>

      {isLoading ? (
        <Skeleton className="h-5 w-14" />
      ) : (
        <span className="text-md-bold">
          {formatPrice(amountDue / 100)}/
          {formatPeriodUnitLabel(selectedPeriodUnit)}
        </span>
      )}
    </div>
  );
}
